import { useState, useEffect } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Crown, Home, ArrowLeft, RefreshCw, Download, X } from "lucide-react";
import SalepageSidebar from "../../components/SalepageSidebar";
import SalepageOverview from "./sections/SalepageOverview";
import SalepageUsers from "./sections/SalepageUsers";
import SalepageCourses from "./sections/SalepageCourses";
import SalepageRevenue from "./sections/SalepageRevenue";
import SalepageDiscounts from "./sections/SalepageDiscounts";
import SalepagePaymentSettings from "./sections/SalepagePaymentSettings";
import SalepageManagement from "./sections/SalepageManagement";
import SalepageMonitoring from "./sections/SalepageMonitoring";
import SalepageReports from "./sections/SalepageReports";

export default function SalepageSystemAdmin() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeSubscriptions: 0,
    totalSalepages: 0,
    totalRevenue: 0,
    revenueThisMonth: 0,
    growthRate: 0,
    newUsersThisMonth: 0,
    pendingTrials: 0,
    pendingPayments: 0,
    mrr: 0,
    arpu: 0,
    churnRate: 0,
  });
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [allSalepages, setAllSalepages] = useState<any[]>([]);
  const [allPayments, setAllPayments] = useState<any[]>([]);
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  const [recentPayments, setRecentPayments] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userSalepages, setUserSalepages] = useState<any[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const adminAuthData = sessionStorage.getItem('admin_authenticated');
    if (adminAuthData) {
      try {
        const { timestamp } = JSON.parse(adminAuthData);
        const now = Date.now();
        const SESSION_DURATION = 3600000; // 1 hour in milliseconds
        
        if (now - timestamp < SESSION_DURATION) {
          loadSalepageData();
          return;
        }
      } catch (e) {
        // Invalid session data, continue to check Firebase user
      }
    }

    if (user?.email !== "viitamint@gmail.com" && user?.role !== "admin") {
      navigate("/");
      return;
    }

    loadSalepageData();
  }, [user, navigate]);

  const loadSalepageData = async () => {
    try {
      // Salepage users live in the salePageSubscriptions collection (source of truth),
      // joined with the users collection for name/email. The old users.salePageSubscription
      // nested field is not populated — that is why the list previously showed 0 users.
      const subsSnap = await getDocs(collection(db, "salePageSubscriptions"));
      const usersAllSnap = await getDocs(collection(db, "users"));
      const userMap: Record<string, any> = {};
      usersAllSnap.docs.forEach(d => { userMap[d.id] = d.data(); });
      const usersData = subsSnap.docs.map(d => {
        const s: any = d.data();
        const uid = s.userId || d.id;
        const u = userMap[uid] || {};
        return {
          uid,
          ...u,
          email: u.email || s.email || '',
          name: u.name || s.name || '',
          salePageSubscription: {
            status: s.status,
            plan: s.plan,
            startDate: s.createdAt,
            endDate: s.subscriptionEndDate || s.trialEndDate,
            trialRequestedAt: s.trialRequestedAt || s.createdAt,
          },
        };
      });
      const totalUsers = usersData.length;
      setAllUsers(usersData);

      const activeSubscriptions = usersData.filter(x =>
        x.salePageSubscription?.status === 'trial' || x.salePageSubscription?.status === 'active'
      ).length;

      // Fetch from both collections
      const paymentsSnap = await getDocs(collection(db, "salePagePayments"));
      const notificationsSnap = await getDocs(collection(db, "salePagePaymentNotifications"));
      
      const paymentsData = paymentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const notificationsData = notificationsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Combine both arrays
      const allPaymentsData = [...paymentsData, ...notificationsData];
      setAllPayments(allPaymentsData);
      
      const verifiedPayments = paymentsSnap.docs.filter(doc => doc.data().status === 'verified');
      const totalRevenue = verifiedPayments.reduce((sum, doc) => sum + (Number(doc.data().amount) || 0), 0);

      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);

      const revenueThisMonth = verifiedPayments
        .filter(doc => {
          const createdAt = doc.data().createdAt?.toDate();
          return createdAt && createdAt >= thisMonthStart;
        })
        .reduce((sum, doc) => sum + (Number(doc.data().amount) || 0), 0);

      const revenueLastMonth = verifiedPayments
        .filter(doc => {
          const createdAt = doc.data().createdAt?.toDate();
          return createdAt && createdAt >= lastMonthStart && createdAt <= lastMonthEnd;
        })
        .reduce((sum, doc) => sum + (Number(doc.data().amount) || 0), 0);

      const growthRate = revenueLastMonth > 0 
        ? ((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100 
        : 0;

      const newUsersThisMonth = usersData.filter(x => {
        const createdAt = x.salePageSubscription?.startDate?.toDate?.() || x.salePageSubscription?.trialRequestedAt?.toDate?.();
        return createdAt && createdAt >= thisMonthStart;
      }).length;

      const salepagesSnap = await getDocs(collection(db, "salepages"));
      const salepagesData = salepagesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllSalepages(salepagesData);
      const totalSalepages = salepagesSnap.size;

      const pendingTrials = usersData.filter(x => x.salePageSubscription?.status === 'trial_pending').length;

      const pendingPaymentsQuery = query(
        collection(db, "salePagePayments"),
        where("status", "==", "pending")
      );
      const pendingPaymentsSnap = await getDocs(pendingPaymentsQuery);
      
      const pendingNotificationsQuery = query(
        collection(db, "salePagePaymentNotifications"),
        where("status", "==", "pending")
      );
      const pendingNotificationsSnap = await getDocs(pendingNotificationsQuery);
      
      const pendingPayments = pendingPaymentsSnap.size + pendingNotificationsSnap.size;

      const recentUsersData = [...usersData]
        .sort((a, b) => {
          const aTime = a.salePageSubscription?.startDate?.toDate?.()?.getTime() ||
                       a.salePageSubscription?.trialRequestedAt?.toDate?.()?.getTime() || 0;
          const bTime = b.salePageSubscription?.startDate?.toDate?.()?.getTime() ||
                       b.salePageSubscription?.trialRequestedAt?.toDate?.()?.getTime() || 0;
          return bTime - aTime;
        })
        .slice(0, 5);
      setRecentUsers(recentUsersData);

      // Combine and sort recent payments from both collections
      const allRecentPayments = [...paymentsSnap.docs, ...notificationsSnap.docs]
        .sort((a, b) => {
          const aTime = a.data().createdAt?.toDate()?.getTime() || 0;
          const bTime = b.data().createdAt?.toDate()?.getTime() || 0;
          return bTime - aTime;
        })
        .slice(0, 5)
        .map(doc => ({ id: doc.id, ...doc.data() }));
      setRecentPayments(allRecentPayments);

      const mrr = activeSubscriptions * 149;
      const arpu = totalUsers > 0 ? totalRevenue / totalUsers : 0;

      const cancelledUsers = usersData.filter(x => {
        const status = x.salePageSubscription?.status;
        const endDate = x.salePageSubscription?.endDate?.toDate?.();
        return status === 'cancelled' && endDate && endDate >= thisMonthStart;
      }).length;
      const churnRate = totalUsers > 0 ? (cancelledUsers / totalUsers) * 100 : 0;

      setStats({
        totalUsers,
        activeSubscriptions,
        totalSalepages,
        totalRevenue,
        revenueThisMonth,
        growthRate,
        newUsersThisMonth,
        pendingTrials,
        pendingPayments,
        mrr,
        arpu,
        churnRate,
      });

      setLoading(false);
    } catch (error) {
      console.error("Error loading salepage data:", error);
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSalepageData();
    setRefreshing(false);
  };

  const handleExportData = () => {
    const csvData = [
      ['Metric', 'Value'],
      ['Total Users', stats.totalUsers],
      ['Active Subscriptions', stats.activeSubscriptions],
      ['Total Salepages', stats.totalSalepages],
      ['Total Revenue', stats.totalRevenue],
      ['Revenue This Month', stats.revenueThisMonth],
      ['Growth Rate', `${stats.growthRate.toFixed(1)}%`],
      ['MRR', stats.mrr],
      ['ARPU', stats.arpu.toFixed(2)],
      ['Churn Rate', `${stats.churnRate.toFixed(1)}%`],
    ];

    const csv = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `salepage-stats-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      verified: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
      trial: 'bg-blue-100 text-blue-800',
      active: 'bg-emerald-100 text-emerald-800',
      trial_pending: 'bg-orange-100 text-orange-800',
    };
    const labels = {
      verified: 'ยืนยันแล้ว',
      pending: 'รอตรวจสอบ',
      rejected: 'ปฏิเสธ',
      trial: 'ทดลองใช้',
      active: 'Active',
      trial_pending: 'รอทดลอง',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status as keyof typeof labels] || status}
      </span>
    );
  };

  const handleUserClick = async (user: any) => {
    setSelectedUser(user);
    const salepagesQuery = query(
      collection(db, "salepages"),
      where("userId", "==", user.uid)
    );
    const salepagesSnap = await getDocs(salepagesQuery);
    const salepages = salepagesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setUserSalepages(salepages);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-600 rounded-xl flex items-center justify-center">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Salepage System Admin</h1>
                  <p className="text-xs text-gray-500">ระบบสร้างหน้าขาย</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">รีเฟรช</span>
              </button>
              <button
                onClick={handleExportData}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span>
              </button>
              <Link
                to="/"
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">หน้าหลัก</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        <div className="hidden lg:block">
          <SalepageSidebar 
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
          />
        </div>

        <div className="flex-1 p-4 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {activeSection === 'overview' && (
              <SalepageOverview
                stats={stats}
                recentUsers={recentUsers}
                recentPayments={recentPayments}
                formatCurrency={formatCurrency}
                getStatusBadge={getStatusBadge}
                onUserClick={handleUserClick}
                navigate={navigate}
              />
            )}
            {activeSection === 'users' && (
              <SalepageUsers
                allUsers={allUsers}
                getStatusBadge={getStatusBadge}
                onUserClick={handleUserClick}
              />
            )}
            {activeSection === 'courses' && (
              <SalepageCourses allSalepages={allSalepages} />
            )}
            {activeSection === 'revenue' && (
              <SalepageRevenue
                stats={stats}
                allPayments={allPayments}
                formatCurrency={formatCurrency}
                getStatusBadge={getStatusBadge}
              />
            )}
            {activeSection === 'discounts' && (
              <SalepageDiscounts />
            )}
            {activeSection === 'payment-settings' && (
              <SalepagePaymentSettings />
            )}
            {activeSection === 'management' && (
              <SalepageManagement
                stats={stats}
                navigate={navigate}
                handleExportData={handleExportData}
              />
            )}
            {activeSection === 'monitoring' && (
              <SalepageMonitoring
                stats={stats}
                recentUsers={recentUsers}
              />
            )}
            {activeSection === 'reports' && (
              <SalepageReports
                stats={stats}
                formatCurrency={formatCurrency}
              />
            )}
          </div>
        </div>
      </div>

      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedUser(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">รายละเอียดผู้ใช้</h2>
                <button onClick={() => setSelectedUser(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-500">ชื่อ</p>
                <p className="font-medium text-gray-900">{selectedUser.displayName || 'ไม่ระบุ'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">อีเมล</p>
                <p className="font-medium text-gray-900">{selectedUser.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Username</p>
                <p className="font-medium text-gray-900">{selectedUser.username || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">สถานะ</p>
                <div className="mt-1">{getStatusBadge(selectedUser.salePageSubscription?.status || 'unknown')}</div>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-2">Salepages ({userSalepages.length})</p>
                {userSalepages.length > 0 ? (
                  <div className="space-y-2">
                    {userSalepages.map((salepage: any, index: number) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg">
                        <p className="font-medium text-gray-900">{salepage.title || 'ไม่มีชื่อ'}</p>
                        <p className="text-sm text-gray-500">{salepage.username}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">ยังไม่มี Salepage</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
